import type {
  BoardStatus,
  ChecklistNodeKind,
  ChecklistNodeLink,
  ChecklistWorkflowStatus,
  TimelineBoardKey,
} from './schema'
import type { ChecklistType } from './checklistBooks'

export const CHECKLIST_CANVAS_NODES_COLLECTION = 'checklist_canvas_nodes' as const
export const CHECKLIST_CANVAS_LAYOUT_VERSION = 2 as const

export type ChecklistCanvasPosition = {
  x: number
  y: number
}

export type ChecklistCanvasDisplayState = {
  title: string
  isCompleted: boolean
  workflowStatus?: ChecklistWorkflowStatus | null
  boardStatus?: BoardStatus | null
  weightage: number
  assigneeId?: string | null
  assigneeName?: string | null
  isHelperVisible?: boolean
  complexityStars?: number
  isUrgentCrisisMode?: boolean
  countdownHours?: number | null
  timerStartedAt?: string | null
  timerExpiredAt?: string | null
  isReady?: boolean
  resourceReadySku?: string | null
}

export type ChecklistCanvasGraphState = {
  prerequisites: string[]
  dependencyNodeIds: string[]
  blockedByNodeIds: ChecklistNodeLink[]
  isBlockerForNodeIds: ChecklistNodeLink[]
  gateInputIds: string[]
}

/**
 * Lightweight canvas projection for drawing, positioning, and dependency logic.
 *
 * Full checklist work/detail data remains in `checklist_items`. Regular checklist
 * nodes point at that detail document with `detailItemId`; pure logic gates may
 * leave it null once gate data no longer needs a legacy `checklist_items` mirror.
 */
export type ChecklistCanvasNode = ChecklistCanvasGraphState & {
  id: string
  workspaceId: string
  parentContainerId: string
  board: TimelineBoardKey
  nodeKind: ChecklistNodeKind
  position: ChecklistCanvasPosition
  workspaceCanvasLayoutVersion: number
  detailItemId: string | null
  legacyChecklistItemId?: string | null
  sourceChecklistId?: string | null
  checklistType?: ChecklistType
  display: ChecklistCanvasDisplayState
  createdById?: string
  createdByName?: string
  createdAt?: unknown
  updatedAt?: unknown
  lastUpdatedBy?: string | null
  lastUpdatedByName?: string | null
}

export type ChecklistCanvasNodeWritePatch = Partial<
  Pick<
    ChecklistCanvasNode,
    | 'position'
    | 'workspaceCanvasLayoutVersion'
    | 'prerequisites'
    | 'dependencyNodeIds'
    | 'blockedByNodeIds'
    | 'isBlockerForNodeIds'
    | 'gateInputIds'
    | 'updatedAt'
    | 'lastUpdatedBy'
    | 'lastUpdatedByName'
  >
>

export type ChecklistCanvasWriteField = keyof ChecklistCanvasNodeWritePatch

export type ChecklistCanvasNodeProjectionPatch = ChecklistCanvasNodeWritePatch &
  Partial<Pick<ChecklistCanvasNode, 'display'>>

export type ChecklistCanvasProjectionWriteField = keyof ChecklistCanvasNodeProjectionPatch

export const CHECKLIST_CANVAS_WRITE_FIELDS = [
  'position',
  'workspaceCanvasLayoutVersion',
  'prerequisites',
  'dependencyNodeIds',
  'blockedByNodeIds',
  'isBlockerForNodeIds',
  'gateInputIds',
  'updatedAt',
  'lastUpdatedBy',
  'lastUpdatedByName',
] as const satisfies readonly ChecklistCanvasWriteField[]

export const CHECKLIST_CANVAS_PROJECTION_WRITE_FIELDS = [
  ...CHECKLIST_CANVAS_WRITE_FIELDS,
  'display',
] as const satisfies readonly ChecklistCanvasProjectionWriteField[]
